import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    MenuItem,
    IconButton,
    Divider,
    useTheme,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Breadcrumbs,
    Link,
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage, useFormikContext, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
import queryGraphql from "../../helpers/ApiInterface";
import MiniBot from "../MiniBot";
import MainDrawer from "../MainDrawer";

// --- Data for Dropdowns based on Cloud Provider ---
const providerData = {
    gcp: {
        regions: ["africa-south1", "asia-east1", "asia-east2", "asia-northeast1", "asia-northeast2", "asia-northeast3", "asia-south1", "asia-south2", "asia-southeast1", "asia-southeast2", "australia-southeast1", "australia-southeast2", "europe-central2", "europe-north1", "europe-southwest1", "europe-west1", "europe-west10", "europe-west12", "europe-west2", "europe-west3", "europe-west4", "europe-west6", "europe-west8", "europe-west9", "me-central1", "me-central2", "me-west1", "northamerica-northeast1", "northamerica-northeast2", "southamerica-east1", "southamerica-west1", "us-central1", "us-east1", "us-east4", "us-east5", "us-south1", "us-west1", "us-west2", "us-west3", "us-west4"],
        models: ["gemini-2.0", "gemini-1.5-pro", "gemini-1.0-pro", "text-bison", "chat-bison", "gemini-pro"],
        gpus: ["0", "1", "2", "4", "8"]
    },
    aws: {
        regions: ["us-east-1", "us-east-2", "us-west-1", "us-west-2", "ca-central-1", "eu-central-1", "eu-west-1", "eu-west-2", "ap-south-1", "ap-northeast-1", "ap-northeast-2", "ap-southeast-1", "ap-southeast-2", "sa-east-1"],
        models: ["anthropic.claude-v2", "amazon.titan-text-express-v1", "cohere.command-text-v14"],
        gpus: ["0", "1", "4", "8"]
    },
    azure: {
        regions: ["East US", "East US 2", "West US", "West US 2", "Central US", "North Europe", "West Europe", "Southeast Asia", "East Asia", "Australia East"],
        models: ["gpt-4", "gpt-35-turbo", "text-davinci-003"],
        gpus: ["0", "1", "2", "4"]
    }
};

const providerDisplayData = [
    { value: 'gcp', label: 'Google Cloud Platform', icon: '/assets/gcp_mini_icon.svg' },
    { value: 'aws', label: 'Amazon Web Services', icon: '/assets/aws_mini_icon.svg' },
    { value: 'azure', label: 'Microsoft Azure', icon: '/assets/azure_mini_icon.svg' }
];

interface CostLabelPair {
    id?: string;
    cost: string;
    label: string;
}

interface AIAgentFormValues {
    id?: string;
    projectId: string;
    region: string;
    imageUrl: string;
    applicationName: string;
    gpu: string;
    agentName: string;
    modelName: string;
    modelTemperature: string;
    modelMaxTokens: string;
    modelStreaming: boolean;
    environment: string;
    cloudProvider: 'gcp' | 'aws' | 'azure';
    costCenterValue: string;
    costLabelPairs: CostLabelPair[];
}

interface ConfigData {
    gcpProjectId: string;
    artifactRegistryName: string;
    awsAccountId?: string;
    azureRegistryName?: string;
}

interface FetchedAgentData {
    id: string;
    agentName: string; 
    environment: string;
}

interface ApplicationData {
    id: string;
    applicationName: string;
    environment: string;
}

interface AddAIAgentPageProps {
    isEdit: boolean;
}
interface AIAgentFormContentProps {
    isEdit: boolean;
}

// --- Helper Functions ---
const generateImageUrl = (values: AIAgentFormValues, config: ConfigData | null) => {
    if (!values.agentName || !values.region || !values.projectId || !config) return "";

    switch (values.cloudProvider) {
        case 'gcp':
            return `${values.region}-docker.pkg.dev/${values.projectId}/${config.artifactRegistryName}/${values.agentName}`;
        case 'aws':
            return `${config.awsAccountId || values.projectId}.dkr.ecr.${values.region}.amazonaws.com/${values.agentName}`;
        case 'azure':
            return `${config.azureRegistryName || values.agentName}.azurecr.io/${values.agentName}`;
        default:
            return "";
    }
};

// --- Formik Helper Components ---
const FormDependentEffects: React.FC<{
    configData: ConfigData | null;
    isEdit: boolean;
    applications: ApplicationData[];
    fetchedAgents: FetchedAgentData[];
    setAvailableEnvironments: (envs: string[]) => void;
}> = ({ configData, isEdit, applications, fetchedAgents, setAvailableEnvironments }) => {
    const { values, setFieldValue, initialValues } = useFormikContext<AIAgentFormValues>();
    const { agentName, region, projectId, applicationName, environment, cloudProvider } = values;
    const prevCloudProviderRef = useRef(cloudProvider);
    const isMounted = useRef(false);
    const {id} = useParams();
    
    useEffect(() => {
        if (prevCloudProviderRef.current !== cloudProvider) {
            setFieldValue("region", "");
            setFieldValue("modelName", "");
            setFieldValue("gpu", "0");
        }
        prevCloudProviderRef.current = cloudProvider;
    }, [cloudProvider, setFieldValue]);

    useEffect(() => {
        if (!isEdit || (isEdit && (values.imageUrl === initialValues.imageUrl || !values.imageUrl))) {
            const newImageUrl = generateImageUrl(values, configData);
            setFieldValue("imageUrl", newImageUrl, false);
        }
    }, [agentName, region, projectId, cloudProvider, configData, setFieldValue, isEdit, initialValues.imageUrl, values.imageUrl]);

    useEffect(() => {
        if (applicationName) {
            const envsForApp = applications
                .filter(app => app.applicationName === applicationName)
                .map(app => app.environment);
            
            const uniqueEnvs = Array.from(new Set(envsForApp));

            const availableEnvs = uniqueEnvs.filter(env => 
                !fetchedAgents.some(agent => 
                    agent.agentName === agentName.toLowerCase() &&
                    agent.environment === env.toLowerCase() &&
                    (!isEdit || id !== agent.id) 
                )
            );
            
            setAvailableEnvironments(availableEnvs);

            if (isMounted.current && !availableEnvs.includes(environment)) {
                setFieldValue('environment', '');
            }
        } else {
            setAvailableEnvironments([]);
            if(isMounted.current) {
                setFieldValue('environment', '');
            }
        }
        
        if(!isMounted.current) {
            isMounted.current = true;
        }
    }, [applicationName, agentName, applications, fetchedAgents, setAvailableEnvironments, setFieldValue, environment, isEdit, id]);

    return null;
};


const AIAgentFormContent: React.FC<AIAgentFormContentProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [configData, setConfigData] = useState<ConfigData | null>(null);
    const [fetchedAgents, setFetchedAgents] = useState<FetchedAgentData[]>([]);
    const [applications, setApplications] = useState<ApplicationData[]>([]);
    const [availableEnvironments, setAvailableEnvironments] = useState<string[]>([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const [initialFormValuesForEdit, setInitialFormValuesForEdit] = useState<AIAgentFormValues | null>(null);
    const [isLoadingAgentData, setIsLoadingAgentData] = useState(isEdit);
    const { user } = useAuth();
    const isViewer = user.roles?.some((role: string) => role === "viewer") || false;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [configRes, agentsRes, appsRes] = await Promise.all([
                    queryGraphql(`query GetConfig { getConfig { gcpProjectId artifactRegistryName } }`),
                    queryGraphql(`query GetSolutionAIAgents { getSolutionAIAgents { id name environment } }`),
                    queryGraphql(`query GetApplications { applications { id applicationName environment } }`)
                ]);

                if (configRes.getConfig) setConfigData(configRes.getConfig);
                if (agentsRes.getSolutionAIAgents) {
                    setFetchedAgents(agentsRes.getSolutionAIAgents.map((agent: any) => ({
                        id: agent.id,
                        agentName: agent.name.toLowerCase(),
                        environment: agent.environment.toLowerCase()
                    })));
                }
                if (appsRes.applications) setApplications(appsRes.applications);

            } catch (error) {
                console.error("Error fetching initial data:", error);
            }
        };
        fetchInitialData();
    }, []);


    useEffect(() => {
        const fetchAgentForEdit = async () => {
             if (isEdit && id) {
                setIsLoadingAgentData(true);
                try {
                    const response = await queryGraphql(`
                        query GetSolutionForEdit($id: String!) {
                          getSolutionAIAgent(id: $id) {
                            id name environment cloudProvider costLabelPairs { cost label }
                            details {
                              aiAgent { projectId region imageUrl gpu modelName modelTemperature modelMaxTokens modelStreaming }
                            }
                            application { applicationName }
                          }
                        }
                    `, { id: id });

                    const agent = response.getSolutionAIAgent;

                    if (agent) {
                        const allPairs: { cost: string, label: string }[] = agent.costLabelPairs || [];
                        const costCenterPair = allPairs.find(p => p.cost === "cost-center");
                        const dynamicPairs = allPairs.filter(p => p.cost !== "cost-center").map((pair, index) => ({ ...pair, id: `clp-edit-${index}` }));

                        setInitialFormValuesForEdit({
                            id: agent.id,
                            agentName: agent.name,
                            environment: agent.environment,
                            cloudProvider: agent.cloudProvider || 'gcp',
                            projectId: agent.details.aiAgent.projectId,
                            region: agent.details.aiAgent.region,
                            imageUrl: agent.details.aiAgent.imageUrl,
                            applicationName: agent.application?.applicationName || "",
                            gpu: agent.details.aiAgent.gpu,
                            modelName: agent.details.aiAgent.modelName,
                            modelTemperature: String(agent.details.aiAgent.modelTemperature),
                            modelMaxTokens: String(agent.details.aiAgent.modelMaxTokens),
                            modelStreaming: agent.details.aiAgent.modelStreaming,
                            costCenterValue: costCenterPair ? costCenterPair.label : "",
                            costLabelPairs: dynamicPairs,
                        });
                    } else {
                        navigate("/aisolutions/aiagent");
                    }
                } catch (err) {
                    console.error("Error fetching agent for editing:", err);
                    navigate("/aisolutions/aiagent");
                } finally {
                    setIsLoadingAgentData(false);
                }
            }
        }
       fetchAgentForEdit();
    }, [isEdit, id, navigate]);


    const currentValidationSchema = Yup.object().shape({
        agentName: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        projectId: Yup.string().required("This field is required").max(50, "Max 50 characters"),
        environment: Yup.string().required("An environment must be selected"),
        region: Yup.string().required("This field is required"),
        cloudProvider: Yup.string().required("This field is required"),
        applicationName: Yup.string().required("This field is required"),
        imageUrl: Yup.string().required("This field is required (auto-generated)"),
        gpu: Yup.string().required("This field is required"),
        modelName: Yup.string().required("This field is required"),
        modelTemperature: Yup.string().matches(/^(0(\.\d+)?|1(\.0+)?|2(\.0+)?|[01])$/, "Must be a number between 0.0 and 2.0").required("This field is required"),
        modelMaxTokens: Yup.string().matches(/^[1-9]\d*$/, "Must be a positive integer ").required("This field is required"),
        modelStreaming: Yup.boolean().required("This field is required"),
        costCenterValue: Yup.string().required("Cost Center value is required").max(63, "Max 63 characters"),
        costLabelPairs: Yup.array().of(Yup.object().shape({
            cost: Yup.string().required("Key is required").max(63, "Max 63 characters").notOneOf(["cost-center"], "'cost-center' is a reserved key."),
            label: Yup.string().required("Value is required").max(63, "Max 63 characters"),
        })),
    });

    const defaultInitialValues: AIAgentFormValues = {
        environment: "",
        agentName: "",
        projectId: configData?.gcpProjectId || "",
        cloudProvider: "gcp",
        region: "",
        applicationName: "",
        imageUrl: "",
        gpu: "0",
        modelName: "",
        modelTemperature: "0.7",
        modelMaxTokens: "1000",
        modelStreaming: true,
        costCenterValue: "",
        costLabelPairs: [],
    };

    if (isEdit && isLoadingAgentData) {
        return <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "50vh" }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading agent data...</Typography></Box>;
    }
    if (isEdit && !initialFormValuesForEdit) {
         return <Typography color="error" sx={{p:3}}>Could not load agent data.</Typography>;
    }

    return (
        <Container sx={{ py: 1, px: { xs: 2, sm: 3, md: 3 }, flexGrow: 1 }}>
            <Formik
                initialValues={isEdit ? (initialFormValuesForEdit || defaultInitialValues) : defaultInitialValues}
                validationSchema={currentValidationSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting, setStatus }) => {
                    setIsSubmittingForm(true);
                    setSubmitting(true);

                    const selectedApplication = applications.find(app => app.applicationName.toLowerCase() === values.applicationName.toLowerCase() && app.environment === values.environment);
                    const applicationId = selectedApplication ? selectedApplication.id : null;
                    
                    const finalCostLabelPairs = [{ cost: "cost-center", label: values.costCenterValue }, ...values.costLabelPairs.map(({ cost, label }) => ({ cost, label }))];

                    const commonInput = {
                        name: values.agentName,
                        environment: values.environment,
                        cloudProvider: values.cloudProvider,
                        costLabelPairs: finalCostLabelPairs,
                        details: {
                            aiAgent: {
                                projectId: values.projectId,
                                region: values.region,
                                imageUrl: values.imageUrl,
                                gpu: values.gpu,
                                modelName: values.modelName,
                                modelTemperature: values.modelTemperature,
                                modelMaxTokens: values.modelMaxTokens,
                                modelStreaming: values.modelStreaming
                            }
                        },
                        application: applicationId,
                    };
                    
                    const mutationString = isEdit ? 
                        `mutation UpdateSolutionAIAgent($input: UpdateSolutionInput) { updateSolutionAIAgent(input: $input) { id } }` :
                        `mutation CreateSolutionAIAgent($input: CreateSolutionInput) { createSolutionAIAgent(input: $input) { id } }`;
                    
                    const variables = { input: isEdit ? { id: id, ...commonInput } : commonInput };
                    const resultKey = isEdit ? 'updateSolutionAIAgent' : 'createSolutionAIAgent';
                    
                    try {
                        const data = await queryGraphql(mutationString, variables);
                        if (data[resultKey] && data[resultKey].id) {
                            navigate("/aisolutions/aiagent");
                        } else {
                            setStatus({ error: `Failed to ${isEdit ? 'update' : 'add'} AI Agent. ${JSON.stringify(data.errors)}` });
                        }
                    } catch (error: any) {
                        setStatus({ error: error.message || "An unexpected error occurred." });
                    } finally {
                        setIsSubmittingForm(false);
                        setSubmitting(false);
                    }
                }}
            >
                {({ handleChange, handleBlur, values, setFieldValue, errors, touched, isValid, dirty, status, isSubmitting }) => {
                    const projectIdLabel = values.cloudProvider === 'aws' ? 'Account ID' : values.cloudProvider === 'azure' ? 'Subscription ID' : 'Project ID';
                    const currentRegionList = providerData[values.cloudProvider]?.regions || [];
                    const currentModelList = providerData[values.cloudProvider]?.models || [];
                    const currentGpuList = providerData[values.cloudProvider]?.gpus || [];
                    const uniqueApplicationNames = Array.from(new Map(applications.map(app => [app.applicationName, app])).values());

                    return (
                        <Form>
                            <FormDependentEffects 
                                configData={configData} 
                                isEdit={isEdit}
                                applications={applications}
                                fetchedAgents={fetchedAgents}
                                setAvailableEnvironments={setAvailableEnvironments}
                            />
                            <Typography fontFamily="Gilroy-Medium" fontSize="16px" mb={2.5}>
                                {isEdit ? "Edit the AI Agent details below" : "Fill the below details to add AI agent"}
                            </Typography>

                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Cloud Provider Details</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <CompulsoryFieldsLabel fieldName="Cloud Provider" />
                                        <ToggleButtonGroup
                                            value={values.cloudProvider}
                                            exclusive
                                            onChange={(event, newProvider) => {
                                                if (newProvider !== null) {
                                                    setFieldValue('cloudProvider', newProvider);
                                                }
                                            }}
                                            aria-label="cloud provider"
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {providerDisplayData.map((p) => (
                                                <ToggleButton
                                                    key={p.value}
                                                    value={p.value}
                                                    aria-label={p.label}
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        textTransform: 'none',
                                                        fontFamily: 'Gilroy-Medium',
                                                        borderRadius: '24px !important',
                                                        border: '1px solid #ccc !important',
                                                        px: 2,
                                                        py: 1,
                                                        '&.Mui-selected': {
                                                            borderColor: '#000 !important',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                        },
                                                        '&.Mui-selected:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                        }
                                                    }}
                                                >
                                                    <img src={p.icon} alt={`${p.label} icon`} style={{ width: '20px', height: '20px' }} />
                                                    {p.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="cloudProvider" /></div>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Basic Agent Details</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3} >
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Agent Name" /><TextField variant="filled" fullWidth name="agentName" value={values.agentName} onChange={handleChange} onBlur={handleBlur} placeholder="Name" size="medium" hiddenLabel error={touched.agentName && Boolean(errors.agentName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="agentName" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName={projectIdLabel} /><TextField variant="filled" fullWidth name="projectId" value={values.projectId} onChange={handleChange} onBlur={handleBlur} placeholder={projectIdLabel} size="medium" hiddenLabel error={touched.projectId && Boolean(errors.projectId)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="projectId" /></div></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CompulsoryFieldsLabel fieldName="Application Name" />
                                        <TextField select variant="filled" fullWidth name="applicationName" value={values.applicationName} onChange={handleChange} onBlur={handleBlur} size="medium" hiddenLabel error={touched.applicationName && Boolean(errors.applicationName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } } }}>
                                            {uniqueApplicationNames.map((app) => (<MenuItem key={app.id} value={app.applicationName} sx={{ fontFamily: "Gilroy-Medium" }}>{app.applicationName}</MenuItem>))}
                                        </TextField>
                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="applicationName" /></div>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CompulsoryFieldsLabel fieldName="Environment" />
                                        <TextField select name="environment" value={values.environment} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" disabled={!values.applicationName || availableEnvironments.length === 0} error={touched.environment && Boolean(errors.environment)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>
                                            <MenuItem value="" disabled>Select an environment</MenuItem>
                                            {availableEnvironments.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}
                                        </TextField>
                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="environment" /></div>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <CompulsoryFieldsLabel fieldName="Region" />
                                        <TextField select name="region" value={values.region} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium"  error={touched.region && Boolean(errors.region)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 250 } } }, }}>
                                            {currentRegionList.map((r) => (<MenuItem key={r} value={r} sx={{ fontFamily: "Gilroy-Medium" }}>{r}</MenuItem>))}
                                        </TextField>
                                        <div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="region" /></div>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Labels</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3} sx={{ mb: "16px" }}>
                                    <Grid item xs={12} sm={6}><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="14px" ml="4px">Key</Typography><TextField fullWidth value="cost-center" disabled variant="filled" hiddenLabel InputProps={{ disableUnderline: true, style: { borderRadius: "8px", backgroundColor: "#f0f0f0", fontFamily: "Gilroy-Medium" } }} /></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Value (Cost Center)" /><TextField fullWidth name="costCenterValue" value={values.costCenterValue} onChange={handleChange} onBlur={handleBlur} placeholder="Enter cost center value" variant="filled" hiddenLabel error={touched.costCenterValue && Boolean(errors.costCenterValue)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="costCenterValue" /></div></Grid>
                                </Grid>
                                <FieldArray name="costLabelPairs" render={arrayHelpers => (
                                    <div>
                                        {values.costLabelPairs.map((pair, index) => (
                                            <Grid container spacing={3} key={pair.id || index} sx={{ mb: "16px" }}>
                                                <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Key" /><TextField fullWidth name={`costLabelPairs.${index}.cost`} value={pair.cost} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom key" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].cost`) && !!getIn(errors, `costLabelPairs[${index}].cost`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.cost`} /></div></Grid>
                                                <Grid item xs={12} sm={5}><CompulsoryFieldsLabel fieldName="Value" /><TextField fullWidth name={`costLabelPairs.${index}.label`} value={pair.label} onChange={handleChange} onBlur={handleBlur} placeholder="Enter custom value" variant="filled" hiddenLabel error={!!getIn(touched, `costLabelPairs[${index}].label`) && !!getIn(errors, `costLabelPairs[${index}].label`)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px" }}><ErrorMessage name={`costLabelPairs.${index}.label`} /></div></Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', pt: { xs: 1, sm: '32px' } }}><IconButton onClick={() => arrayHelpers.remove(index)}><img alt="remove-icon" src="/assets/close-icon.svg" /></IconButton></Grid>
                                            </Grid>
                                        ))}
                                        <Button variant="text" sx={{ color: "#000000", textTransform: "none", fontFamily: "Gilroy-Medium" }} onClick={() => arrayHelpers.push({ cost: "", label: "", id: `new-${Math.random()}` })}>
                                            + Add Key Value Pair
                                        </Button>
                                    </div>
                                )} />
                            </Box>

                            <Box sx={{ backgroundColor: "white", p: "24px", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0,0,0,0.05)", mb: "24px" }}>
                                <Typography fontFamily="Gilroy-Medium" fontSize="14px" sx={{ mb: "16px" }}>Model Details</Typography>
                                <Divider sx={{ mb: "24px" }} />
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="GPU" /><TextField select name="gpu" value={values.gpu} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.gpu && Boolean(errors.gpu)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}>{currentGpuList.map((g) => (<MenuItem key={g} value={g} sx={{ fontFamily: "Gilroy-Medium" }}>{g}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="gpu" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Model Name" /><TextField select name="modelName" value={values.modelName} onChange={handleChange} onBlur={handleBlur} variant="filled" fullWidth hiddenLabel size="medium" error={touched.modelName && Boolean(errors.modelName)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }}>{currentModelList.map((m) => (<MenuItem key={m} value={m} sx={{ fontFamily: "Gilroy-Medium" }}>{m}</MenuItem>))}</TextField><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="modelName" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Model Temperature" /><TextField variant="filled" fullWidth name="modelTemperature" value={values.modelTemperature} onChange={handleChange} onBlur={handleBlur} placeholder="Between (0.0-2.0)" size="medium" hiddenLabel error={touched.modelTemperature && Boolean(errors.modelTemperature)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="modelTemperature" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Model Tokens" /><TextField variant="filled" fullWidth name="modelMaxTokens" value={values.modelMaxTokens} onChange={handleChange} onBlur={handleBlur} placeholder="Tokens upto" size="medium" hiddenLabel error={touched.modelMaxTokens && Boolean(errors.modelMaxTokens)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px", fontFamily: "Gilroy-Medium" } }} /><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="modelMaxTokens" /></div></Grid>
                                    <Grid item xs={12} sm={6}><CompulsoryFieldsLabel fieldName="Model Streaming" /><Box display="flex" gap={1} mt="8px"><ToggleButtonGroup value={values.modelStreaming} exclusive onChange={(event, newValue) => { if (newValue !== null) { setFieldValue("modelStreaming", newValue); } }} aria-label="Model Streaming" size="small"><ToggleButton value={true} aria-label="true" sx={{ mx: 3, borderRadius: '20px !important', borderColor: values.modelStreaming === true ? "black !important" : "#cccccc", backgroundColor: values.modelStreaming === true ? "#e0e0e0" : "white", color: values.modelStreaming === true ? "black" : "#555555", textTransform: "none", fontFamily: "Gilroy-Medium", px: 3, '&.Mui-selected': { backgroundColor: "#c0c0c0", borderColor: "black !important", color: "black" }, '&.Mui-selected:hover': { backgroundColor: "#b0b0b0" } }}>True</ToggleButton><ToggleButton value={false} aria-label="false" sx={{ borderRadius: '20px !important', borderColor: values.modelStreaming === false ? "black !important" : "#cccccc", backgroundColor: values.modelStreaming === false ? "#e0e0e0" : "white", color: values.modelStreaming === false ? "black" : "#555555", textTransform: "none", fontFamily: "Gilroy-Medium", px: 3, '&.Mui-selected': { backgroundColor: "#c0c0c0", borderColor: "black !important", color: "black" }, '&.Mui-selected:hover': { backgroundColor: "#b0b0b0" } }}>False</ToggleButton></ToggleButtonGroup></Box><div style={{ color: "red", marginTop: "8px", marginLeft: "4px" }}><ErrorMessage name="modelStreaming" /></div></Grid>
                                </Grid>
                                 <FormControlLabel control={<Checkbox checked={confirmationChecked} onChange={(e) => setConfirmationChecked(e.target.checked)} name="confirmationCheckbox" sx={{ color: 'black', '&.Mui-checked': { color: 'black', }, }} />} label="I confirm that the details provided are correct and I wish to proceed." sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-start', fontFamily: "Gilroy-Medium", '& .MuiTypography-root': {fontFamily: "Gilroy-Medium"} }} />
                            </Box>

                            {status && status.error && (<Typography color="error" sx={{ fontFamily: "Gilroy-Medium", mb: 2, textAlign: 'center' }}>{status.error}</Typography>)}
                            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                                <Button sx={{ color: "black", borderColor: "black", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} variant="outlined" size="large" onClick={() => navigate("/aisolutions/aiagent")} disabled={isSubmittingForm || isSubmitting}>Cancel</Button>
                                <Button sx={{ backgroundColor: "black", color: "white", fontWeight: "bold", textTransform: "none", px: 4, fontFamily: "Gilroy-Bold", "&:hover": { backgroundColor: "#333333" }, "&.Mui-disabled": { backgroundColor: "#cccccc", color: "#888888" } }} variant="contained" size="large" type="submit" disabled={isSubmittingForm || isSubmitting || !(isValid && dirty && confirmationChecked)  || isViewer } >
                                    {(isSubmittingForm || isSubmitting) ? <CircularProgress size={24} color="inherit" /> : (isEdit ? "Save Changes" : "Add New AI Agent")}
                                </Button>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>
        </Container>
    );
};

const AddAIAgentPage: React.FC<AddAIAgentPageProps> = ({ isEdit }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };
    

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"aisolutions-aiagent"} onStateChange={handleDrawerStateChange} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: '#fafafa',
                    transition: 'margin 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms', // Simplified transition
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ mb: 3, ml: {xs:2, sm:3, md:5} , mt:3 }}>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "#555555", fontFamily: "Gilroy-Medium" }}>
                        <Link underline="hover" color="inherit" href="/aisolutions/aiagent" sx={{ fontFamily: "Gilroy-Medium" }} onClick={(e)=> {e.preventDefault(); navigate("/aisolutions/aiagent")}}>AI Agents</Link>
                        <Typography color="text.primary" sx={{ fontFamily: "Gilroy-Medium" }}>
                            {isEdit ? "Edit AI Agent" : "Add New AI Agent"}
                        </Typography>
                    </Breadcrumbs>
                    <Box display="flex" alignItems="center" mt={1.5}>
                        <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/aiagent")} sx={{ mr: 0.5 }}><ArrowBackIosNewIcon fontSize="small" /></IconButton>
                        <Typography fontFamily="Gilroy-Bold" fontSize="20px">
                             {isEdit ? "Edit AI Agent" : "Add New AI Agent"}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1, alignItems: 'flex-start', pt:0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: { xs: '95%', md: '80%' }, py: 0 }}>
                        <AIAgentFormContent isEdit={isEdit} />
                    </Box>
                </Box>
                <MiniBot />
            </Box>
        </Box>
    );
};

export default AddAIAgentPage;